import React from 'react'
import { useTheme } from '@mui/material/styles'

import type { DataSet } from '../../../../types/Charts/BarChart'
import { stripChartSlotProps } from '../../../../../common/components/charts/chartSlotProps'

interface BarStyleProps extends React.CSSProperties {
  x?: number
  y?: number
}

interface CustomDoubleBarPlotProps {
  color: string
  data: DataSet[]
  series: Series
  style: BarStyleProps
}

interface Series {
  index: number
  type: number
}

export const CustomDoubleBarPlot = ({
  color,
  data,
  series,
  style,
  ...restProps
}: CustomDoubleBarPlotProps) => {
  const theme = useTheme()
  const rectRef = React.useRef<SVGRectElement>(null)
  const textRef = React.useRef<SVGTextElement>(null)
  const [barWidth, setBarWidth] = React.useState((style.width as number) || 0)

  React.useLayoutEffect(() => {
    setBarWidth((style.width as number) || 0)
  }, [style.width])

  React.useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (rectRef.current) {
          const { width } = rectRef.current.getBoundingClientRect()

          const xValue = width / 2
          const textElement = rectRef.current.querySelector('text')
          if (textElement) {
            textElement.setAttribute('transform', `translate(${xValue}, -8)`)
          }
        }
      })
    })

    if (rectRef.current) resizeObserver.observe(rectRef.current)
    return () => {
      if (rectRef.current) resizeObserver.unobserve(rectRef.current)
    }
  }, [])

  const { ariaLabelComparison, ariaLabelMain, formattedComparison, formattedMain } =
    data[series.index]

  const ariaLabel = series.type === 0 ? ariaLabelMain : ariaLabelComparison
  const formattedAmount = series.type === 0 ? formattedMain : formattedComparison

  const rectStyle = {
    ...style,
    transition: 'all 0.3s ease-in-out',
  }

  const textTransform = `translate(${barWidth / 2}, -5)`

  return (
    <g aria-label={ariaLabel}>
      <rect
        {...stripChartSlotProps(restProps)}
        fill={color}
        ref={rectRef}
        rx={2}
        style={rectStyle}
      />
      <text
        fill={theme.palette.text.secondary}
        fontSize={11}
        fontWeight={400}
        ref={textRef}
        style={{ transition: 'transform 0.3s ease-in-out' }}
        textAnchor="middle"
        transform={textTransform}
        x={style.x}
        y={style.y}
      >
        {formattedAmount}
      </text>
    </g>
  )
}
